import { takeEvery, takeLatest, put, call, all, fork, select } from "@redux-saga/core/effects";
import { addErrorLog } from "../../../Logger/logger";
import {
  CREATE_USER_COMPONENT_GROUP,
  UPDATE_USER_COMPONENT_GROUP,
  FETCH_USER_COMPONENT_GROUPS,
  FETCH_COMPONENT_GROUP_COMPONENTS,
  FETCH_ACTIVE_USER_COMPONENT_GROUPS,
  UPDATE_USER_COMPONENT_GROUPS_STATE,
  DELETE_USER_COMPONENT_GROUP,
  SET_DELETE_USER_COMPONENT_GROUP_SUCCESS,
  FETCH_TOOLBOX_DATA
} from "redux/constants/flowDesigner";
import {
  fetchUserComponentGroups,
  createUserComponentGroup,
  updateUserComponentGroup,
  fetchComponentGroupComponents,
  deleteUserComponentGroup
} from "api/FlowDesignerApis";
import {
  setUserComponentGroups,
  setComponentGroupComponents,
  setActiveUserComponentGroups,
  addUserComponentGroup
} from "redux/actions/flowDesigner/componentGroupsActionCreator";

/***** WORKERS *****/

export function* handleFetchComponentGroupComponents({ payload }) {
  try {
    const { tenant_id } = payload;
    const components = yield call(fetchComponentGroupComponents, tenant_id);
    if (components) {
      yield put(setComponentGroupComponents(components));
    }
  } catch (error) {
    addErrorLog("handleFetchComponentGroupComponents", error);
  }
}

export function* handleFetchUserComponentGroups({ payload }) {
  try {
    const { tenant_id } = payload;
    const groups = yield call(fetchUserComponentGroups, tenant_id);

    if (groups) {
      const { components } = yield select((state) => state?.designer?.cxfComponents);
      const { componentGroupComponents } = yield select(
        (state) => state?.designer?.componentGroups
      );

      if (groups && groups.length) {
        groups.forEach((group) => {
          const compArray = [];
          const componentIds = [];
          const comps = componentGroupComponents?.filter(
            (c) => c.user_component_groups_id === group.id
          );

          if (comps.length) {
            comps.forEach((c) => {
              if (!componentIds.includes(c.component_id)) {
                // temporary fix to prevent duplicates
                componentIds.push(c.component_id);
                const component = components.find((cmp) => cmp.id === c.component_id);
                if (component) {
                  compArray.push({
                    id: component.id,
                    is_hidden: c.is_hidden,
                    name: component.name,
                    component_id: component.id
                  });
                }
              }
            });
          }

          group.components = compArray;
        });
      }
      groups.unshift({
        id: "-1",
        name: "All Components",
        is_hidden: true,
        components: components.map((c) => {
          return {
            id: c.id,
            is_hidden: c.is_hidden,
            name: c.name
          };
        })
      });
      yield put(setUserComponentGroups(groups));
    }
  } catch (error) {
    addErrorLog("handleFetchUserComponentGroups", error);
  }
}

export function* handleFetchActiveUserComponentGroups({ payload }) {
  try {
    const { tenant_id } = payload;
    const groups = yield call(fetchUserComponentGroups, tenant_id);
    if (groups) {
      const activeGroups = groups.filter((g) => !g.is_hidden);
      yield put(setActiveUserComponentGroups(activeGroups));
    }
  } catch (error) {
    addErrorLog("handleFetchActiveUserComponentGroups", error);
  }
}

export function* handleCreateUserComponentGroup({ payload }) {
  try {
    const { tenant_id: tenant_id_payload, input } = payload;
    const group = yield call(createUserComponentGroup, tenant_id_payload, input);
    const { tenant_id, ...restGroup } = Object.assign(group, input);

    if (!restGroup.component_groups) {
      restGroup.component_groups = [];
    }

    yield put(addUserComponentGroup(restGroup));
    yield handleFetchActiveUserComponentGroups({ payload });
    yield handleFetchToolboxData({ payload });
  } catch (error) {
    addErrorLog("handleCreateUserComponentGroup", error);
  }
}

export function* handleUpdateUserComponentGroup({ payload }) {
  try {
    const { tenant_id, group_id, input, type } = payload;
    yield call(updateUserComponentGroup, tenant_id, group_id, input);

    if (type === "sidebar") {
      yield handleFetchActiveUserComponentGroups({ payload });
    } else if (type === "componentSelection") {
      yield handleFetchToolboxData({ payload });
    }
  } catch (error) {
    addErrorLog("handleUpdateUserComponentGroup", error);
  }
}

export function* handleUpdateUserComponentGroupsState({ payload }) {
  try {
    const { tenant_id } = payload;
    const { userComponentGroups } = yield select((state) => state?.designer?.componentGroups);
    for (const group of userComponentGroups) {
      const { id, name, user_id, components, is_hidden } = group;
      if (id !== "-1") {
        yield call(updateUserComponentGroup, tenant_id, id, {
          name,
          user_id,
          is_hidden,
          component_groups: components?.length
            ? components.map((c) => {
                return { component_id: c.id, is_hidden: c.is_hidden };
              })
            : []
        });
      }
    }
    yield handleFetchToolboxData({ payload });
  } catch (error) {
    addErrorLog("handleUpdateUserComponentGroupsState", error);
  }
}

export function* handleDeleteUserComponentGroup({ payload }) {
  try {
    const { tenant_id, group_id } = payload;
    yield call(deleteUserComponentGroup, tenant_id, group_id);
    yield put({ type: SET_DELETE_USER_COMPONENT_GROUP_SUCCESS });
    yield handleFetchToolboxData({ payload });
  } catch (error) {
    addErrorLog("handleDeleteUserComponentGroup", error);
  }
}

export function* handleFetchToolboxData(data) {
  try {
    yield handleFetchActiveUserComponentGroups(data);
    yield handleFetchComponentGroupComponents(data);
  } catch (error) {
    addErrorLog("handleFetchToolboxData", error);
  }
}

/***** WATCHERS *****/

export function* watchFetchComponentGroupComponents() {
  yield takeEvery(FETCH_COMPONENT_GROUP_COMPONENTS, handleFetchComponentGroupComponents);
}
export function* watchFetchUserComponentGroups() {
  yield takeEvery(FETCH_USER_COMPONENT_GROUPS, handleFetchUserComponentGroups);
}
export function* watchFetchActiveUserComponentGroups() {
  yield takeEvery(FETCH_ACTIVE_USER_COMPONENT_GROUPS, handleFetchActiveUserComponentGroups);
}
export function* watchCreateUserComponentGroup() {
  yield takeEvery(CREATE_USER_COMPONENT_GROUP, handleCreateUserComponentGroup);
}
export function* watchUpdateUserComponentGroup() {
  yield takeLatest(UPDATE_USER_COMPONENT_GROUP, handleUpdateUserComponentGroup);
}
export function* watchUpdateUserComponentGroupsState() {
  yield takeEvery(UPDATE_USER_COMPONENT_GROUPS_STATE, handleUpdateUserComponentGroupsState);
}
export function* watchDeleteUserComponentGroup() {
  yield takeEvery(DELETE_USER_COMPONENT_GROUP, handleDeleteUserComponentGroup);
}
export function* watchFetchToolboxData() {
  yield takeEvery(FETCH_TOOLBOX_DATA, handleFetchToolboxData);
}

export function* rootComponentGroupsSaga() {
  yield all([
    fork(watchFetchComponentGroupComponents),
    fork(watchFetchActiveUserComponentGroups),
    fork(watchFetchUserComponentGroups),
    fork(watchCreateUserComponentGroup),
    fork(watchUpdateUserComponentGroup),
    fork(watchUpdateUserComponentGroupsState),
    fork(watchDeleteUserComponentGroup),
    fork(watchFetchToolboxData)
  ]);
}
